import type { Ref } from 'vue'

interface CursorState {
  selectedTool: string
  currentColor: string
  brushSize: number
}

export function useCursor(pixelSize: number, scale: Ref<number>) {
  // Create custom cursor
  const createCustomCursor = (color: string, size: number, currentScale: number) => {
    const cursorCanvas = document.createElement('canvas')
    const scaledPixelSize = pixelSize * currentScale
    const cursorSize = size * scaledPixelSize
    cursorCanvas.width = cursorSize
    cursorCanvas.height = cursorSize

    const cursorCtx = cursorCanvas.getContext('2d')
    if (!cursorCtx) return ''

    // Clear background
    cursorCtx.clearRect(0, 0, cursorSize, cursorSize)

    // Create a pixelated circle
    const radius = size / 2
    const centerX = size / 2
    const centerY = size / 2

    // Fill pixels that form a circle
    for (let x = 0; x < size; x++) {
      for (let y = 0; y < size; y++) {
        // Calculate distance from center
        const dx = x + 0.5 - centerX
        const dy = y + 0.5 - centerY
        const distance = Math.sqrt(dx * dx + dy * dy)

        // If pixel is within radius, fill it
        if (distance <= radius) {
          cursorCtx.fillStyle = color
          cursorCtx.fillRect(
            x * scaledPixelSize,
            y * scaledPixelSize,
            scaledPixelSize,
            scaledPixelSize,
          )
        }
      }
    }

    // Draw white border around the circle
    cursorCtx.strokeStyle = 'white'
    cursorCtx.lineWidth = currentScale
    for (let x = 0; x < size; x++) {
      for (let y = 0; y < size; y++) {
        const dx = x + 0.5 - centerX
        const dy = y + 0.5 - centerY
        const distance = Math.sqrt(dx * dx + dy * dy)

        if (Math.abs(distance - radius) < 1) {
          cursorCtx.strokeRect(
            x * scaledPixelSize,
            y * scaledPixelSize,
            scaledPixelSize,
            scaledPixelSize,
          )
        }
      }
    }

    // Draw black outer border
    cursorCtx.strokeStyle = 'black'
    cursorCtx.lineWidth = currentScale
    for (let x = 0; x < size; x++) {
      for (let y = 0; y < size; y++) {
        const dx = x + 0.5 - centerX
        const dy = y + 0.5 - centerY
        const distance = Math.sqrt(dx * dx + dy * dy)

        if (Math.abs(distance - radius) < 1) {
          cursorCtx.strokeRect(
            x * scaledPixelSize - currentScale / 2,
            y * scaledPixelSize - currentScale / 2,
            scaledPixelSize + currentScale,
            scaledPixelSize + currentScale,
          )
        }
      }
    }

    return cursorCanvas.toDataURL()
  }

  // Update cursor style
  const updateCursor = (canvasElement: HTMLCanvasElement | null, cursorState: CursorState) => {
    if (!canvasElement) return

    if (cursorState.selectedTool === 'brush' || cursorState.selectedTool === 'eraser') {
      const cursorColor = cursorState.selectedTool === 'brush' ? cursorState.currentColor : ''
      const cursorUrl = createCustomCursor(cursorColor, cursorState.brushSize, scale.value)
      const scaledSize = cursorState.brushSize * pixelSize * scale.value
      canvasElement.style.cursor = `url(${cursorUrl}) ${scaledSize / 2} ${scaledSize / 2}, auto`
    } else {
      canvasElement.style.cursor = 'default'
    }
  }

  return {
    updateCursor
  }
}
